import type { LucideIcon } from "lucide-react"
import { useRef } from "react"
import { Button } from "@/primitives/button.tsx"
import { cn } from "@/primitives/utils"
import { useDockedBar } from "./save-bar-dock.ts"
import { useKeyboardUp, useViewport } from "./viewport.tsx"

/** One thing a page does to the rows chosen, such as Download. */
export type SelectionAction = {
  label: string
  icon?: LucideIcon
  onSelect?: () => void
  disabled?: boolean
}

/*
 * The bar a list raises while rows in it are chosen (`DataTable`'s `selection`), modelled on Chat's
 * message-choosing bar: "{n} selected", then what the page does to them, then Clear, which unchooses
 * every one. The page renders it only while something is chosen. The count is said to a screen
 * reader as it changes.
 *
 * On the desk it floats at the foot of the list as the Member scrolls, as a SaveBar does. On
 * `compact` it is the full width, fixed at the foot of the screen: docked on the tab bar, or on the
 * on-screen keyboard while it is up, with the page's other docked bars (`useDockedBar`).
 */
export function SelectionBar({
  count,
  actions,
  onClear,
}: {
  count: number
  actions: readonly SelectionAction[]
  onClear: () => void
}) {
  const bar = useRef<HTMLDivElement>(null)
  const { compact } = useViewport()
  const keyboardUp = useKeyboardUp()
  const below = useDockedBar(bar, compact)
  return (
    <div
      ref={bar}
      role="region"
      aria-label="Selection"
      data-slot="selection-bar"
      // A fixed bar's bottom margin lifts it off the foot, above the bars docked below it.
      style={below > 0 ? { marginBottom: below } : undefined}
      className={cn(
        compact
          ? [
              "fixed inset-x-0 z-30 flex min-h-14 items-center gap-2 border-t border-border-subtle bg-background px-4 py-2",
              keyboardUp ? "bottom-keyboard" : "bottom-shell",
            ]
          : "sticky bottom-4 z-20 mx-auto flex h-11 w-fit animate-in items-center gap-3 rounded-lg bg-popover px-3 shadow-dialog duration-150 ease-snappy fade-in slide-in-from-bottom-2 touch:h-auto touch:min-h-11 touch:py-1.5",
      )}
    >
      <p role="status" className="text-ui font-medium tabular-nums compact:flex-1">
        {count} selected
      </p>
      <div className="flex shrink-0 items-center gap-1.5">
        {actions.map(({ label, icon: Icon, onSelect, disabled }) => (
          <Button
            key={label}
            size="sm"
            variant="outline"
            className="touch:h-11"
            disabled={disabled}
            onClick={onSelect}
          >
            {Icon && <Icon data-icon="inline-start" />}
            {label}
          </Button>
        ))}
        <Button size="sm" variant="ghost" className="touch:h-11" onClick={onClear}>
          Clear
        </Button>
      </div>
    </div>
  )
}
